import { useCallback, useEffect, useMemo, useState } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';

import {
	type UseFetchConfigAnalytics,
	useThirdPartyConfig,
} from '@atlassian/rovo-connectors-config/hooks';

import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { getMonitoringClient } from '@confluence/monitoring/entry-points/getMonitoringClient.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

const QUICK_INSERT_EMBEDS_EXPERIENCE_ID = 'confluenceQuickInsertEmbeds';

/**
 * Schedules `cb` to run once the browser is idle, keeping the connector-config
 * fetch off the editor's initial-load (VC) critical path. Falls back to a macro
 * task where `requestIdleCallback` is unavailable. Returns a cleanup function
 * that cancels the pending callback.
 */
const deferUntilIdle = (cb: () => void): (() => void) => {
	if (typeof window === 'undefined') {
		return () => {};
	}
	const win = window as Window & {
		requestIdleCallback?: (callback: () => void, options?: { timeout: number }) => number;
		cancelIdleCallback?: (id: number) => void;
	};
	if (typeof win.requestIdleCallback === 'function') {
		const id = win.requestIdleCallback(cb, { timeout: 5000 });
		return () => win.cancelIdleCallback?.(id);
	}
	const id = win.setTimeout(cb, 0);
	return () => win.clearTimeout(id);
};

/**
 * Returns the set of lower-cased third-party product keys the user has an active
 * connection to, sourced live from the search connector config — the same source
 * the Rovo Link Picker relies on.
 *
 * Unlike a passive read of the search-config localStorage cache (which is not
 * populated on the editor route), this hook actively triggers `useThirdPartyConfig`,
 * so it reflects the user's current connection state, including disconnects.
 *
 * A connector counts as "connected" when its config entry has `userNeedsOAuth !== true`
 * (matching the link picker's "usable without an auth prompt" semantics). Connectors
 * the tenant has installed but the user has not authenticated — or has since
 * disconnected — report `userNeedsOAuth: true` and are excluded.
 *
 * Returns `undefined` while the config is still loading (or when `enabled` is false)
 * so callers can fail open and avoid hiding products before the connection state is
 * known.
 *
 * The fetch is deferred until the browser is idle so it stays off the editor's
 * initial-load (VC) critical path — the quick-insert menu isn't interactable
 * during initial paint, so connection state isn't needed until later. Until the
 * deferred fetch resolves the hook returns `undefined`, so the caller fails open
 * and shows all apps in the interim.
 *
 * NOTE: This only covers third-party connectors (drive, figma, amplitude, sharepoint,
 * …). First-party / Atlassian products such as Loom never appear in the third-party
 * config and must be handled separately by the caller.
 *
 * @param enabled When false, the hook is fully disabled (no fetch) and returns
 *   `undefined`. Used to keep the request scoped to the experiment cohort.
 */
export function useConnectedThirdPartyProductKeys(enabled: boolean): Set<string> | undefined {
	const { cloudId, userId } = useSessionData();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	// Gate the fetch behind an idle callback so it doesn't run during the
	// initial-load critical path. `hasReachedIdle` flips to true once the
	// browser is idle (or after the fallback macro task), at which point the
	// fetch is allowed to start.
	const [hasReachedIdle, setHasReachedIdle] = useState(false);

	useEffect(() => {
		if (!enabled || !cloudId || !userId) {
			return;
		}
		return deferUntilIdle(() => setHasReachedIdle(true));
	}, [enabled, cloudId, userId]);

	const fireAnalyticsEvent: UseFetchConfigAnalytics['fireAnalyticsEvent'] = useCallback(
		(event) => createAnalyticsEvent(event).fire(),
		[createAnalyticsEvent],
	);

	const logException: UseFetchConfigAnalytics['logException'] = useCallback((exception) => {
		getMonitoringClient().submitError(exception, {
			attribution: Attribution.INTEGRATIONS,
			partialFailure: true,
		});
	}, []);

	const analytics: UseFetchConfigAnalytics = useMemo(
		() => ({
			searchSessionId: QUICK_INSERT_EMBEDS_EXPERIENCE_ID,
			source: QUICK_INSERT_EMBEDS_EXPERIENCE_ID,
			actionSubject: 'quickInsertEmbeds',
			taskNamePrefix: QUICK_INSERT_EMBEDS_EXPERIENCE_ID,
			fireAnalyticsEvent,
			logException,
		}),
		[fireAnalyticsEvent, logException],
	);

	const disabled = !enabled || !cloudId || !userId || !hasReachedIdle;
	const { thirdPartyConfigs } = useThirdPartyConfig({
		cloudId,
		userId,
		disabled,
		analytics,
	});

	// Derive a stable signature first so the returned Set keeps a stable identity
	// until the connected products actually change. The caller threads this value
	// into a memoised provider list, so a fresh Set on every render would rebuild
	// the editor's extension providers needlessly.
	const signature = useMemo(() => {
		if (disabled || !thirdPartyConfigs) {
			return undefined;
		}
		return Object.entries(thirdPartyConfigs)
			.filter(([, config]) => config?.userNeedsOAuth !== true)
			.map(([key]) => key.toLowerCase())
			.sort()
			.join(',');
	}, [disabled, thirdPartyConfigs]);

	return useMemo(() => {
		if (signature === undefined) {
			return undefined;
		}
		return new Set(signature ? signature.split(',') : []);
	}, [signature]);
}
